// Read hooks. Query keys include the signed-in identity (demo: sandbox + persona;
// accounts: the auth user id): a different identity sees different data and different
// available actions, and never another person's cached permit or training record.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, useSyncExternalStore } from 'react'
import type {
  BoardEntry,
  CommandArgs,
  CommandName,
  CommandResult,
  LearningOverview,
  LearningProgressRecord,
  LicenceRegister,
  Me,
  PermitBundle,
  PermitSummary,
  PolicyView,
  Registry,
} from '@/domain/types'
import type { AccountAdminView } from '@/domain/identity'
import type { SiteRegistryView } from '@/domain/registry'
import { callRead, newIdempotencyKey, PtwError, runCommand } from './commands'
import { getSnapshot, subscribe } from './session'

export function useSession() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Cache identity (src/lib/identity.ts identityKey). A different identity sees different data. */
function usePersonaKey(): string {
  return useSession().identityKey ?? 'none'
}

export function useMe() {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['me', persona],
    queryFn: () => callRead<Me>('ptw_me'),
    enabled: persona !== 'none',
    staleTime: 30_000,
  })
}

export function usePermits(filter: Record<string, unknown> = {}) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['permits', persona, filter],
    queryFn: () => callRead<PermitSummary[]>('ptw_list_permits', { p_filter: filter }),
    enabled: persona !== 'none',
    refetchInterval: 30_000,
  })
}

/** Permit bundle; refreshed every 30 s so eligibility is re-evaluated at server time. */
export function usePermit(permitId: string | undefined) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['permit', persona, permitId],
    queryFn: () => callRead<PermitBundle>('ptw_get_permit', { p_permit_id: permitId }),
    enabled: persona !== 'none' && Boolean(permitId),
    refetchInterval: 30_000,
  })
}

export function useBoard(filter: Record<string, unknown> = {}) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['board', persona, filter],
    queryFn: () => callRead<BoardEntry[]>('ptw_board', { p_filter: filter }),
    enabled: persona !== 'none',
    refetchInterval: 30_000,
  })
}

export function useRegistry() {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['registry', persona],
    queryFn: () => callRead<Registry>('ptw_registry'),
    enabled: persona !== 'none',
    staleTime: 5 * 60_000,
  })
}

export function usePolicy(versionId?: string | null) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['policy', persona, versionId ?? 'current'],
    queryFn: () => callRead<PolicyView>('ptw_policy', { p_policy_version_id: versionId ?? null }),
    enabled: persona !== 'none',
    staleTime: 5 * 60_000,
  })
}

/** The signed-in person's saved Help & Learning progress for one content edition (null if none). */
export function useLearningProgress(contentId: string, contentVersion: string) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['learning-progress', persona, contentId, contentVersion],
    queryFn: () =>
      callRead<LearningProgressRecord | null>('ptw_learning_progress_get', {
        p_content_id: contentId,
        p_content_version: contentVersion,
      }),
    enabled: persona !== 'none',
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

/** Account administrators: everyone's Help & Learning progress on the sites they can see. */
export function useLearningOverview(contentId: string, contentVersion: string, enabled = true) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['learning-overview', persona, contentId, contentVersion],
    queryFn: () =>
      callRead<LearningOverview>('ptw_learning_overview', { p_content_id: contentId, p_content_version: contentVersion }),
    enabled: enabled && persona !== 'none',
    refetchInterval: 30_000,
  })
}

/** Account administrators: the licence register of the sites they can see (0021). */
export function useLicenceRegister(enabled = true) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['licence-register', persona],
    queryFn: () => callRead<LicenceRegister>('ptw_licence_register'),
    enabled: enabled && persona !== 'none',
    refetchInterval: 60_000,
  })
}

/** Registry administrators: Sites & units (0029). */
export function useSiteRegistry(enabled = true) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['site-registry', persona],
    queryFn: () => callRead<SiteRegistryView>('ptw_site_registry'),
    enabled: enabled && persona !== 'none',
    refetchInterval: 60_000,
  })
}

/** Account administrators: People & access on the sites they can see (0022). */
export function useAccountAdminView(enabled = true) {
  const persona = usePersonaKey()
  return useQuery({
    queryKey: ['account-admin', persona],
    queryFn: () => callRead<AccountAdminView>('ptw_account_admin_view'),
    enabled: enabled && persona !== 'none',
    refetchInterval: 60_000,
  })
}

/**
 * The permit version a person was shown when an action panel opened. The permit page
 * refreshes every 30 s, so a panel's copy can change under the signer. Commands are sent
 * with the version the signer saw, and a change while the panel is open is shown and must
 * be acknowledged before signing: nobody signs over a state they have not read. (Sending
 * the page's latest version instead would let the refreshed copy pass unseen; the server
 * refuses a version that is out of date with PW002, which also triggers a re-read here.)
 */
export function useSeenVersion(bundle: PermitBundle) {
  const permit = usePermit(bundle.permit.id)
  const [seen, setSeen] = useState(bundle.permit.row_version)
  const changed = bundle.permit.row_version !== seen
  const changes = changed ? bundle.events.filter((e) => (e.new_row_version ?? 0) > seen) : []
  return {
    version: seen,
    changed,
    changes,
    acknowledge: () => setSeen(bundle.permit.row_version),
    /** After PW002 (stale version): read the permit again so the change can be shown. */
    reread: () => void permit.refetch(),
  }
}
export type SeenVersion = ReturnType<typeof useSeenVersion>

/**
 * A command bound to one user intent. The idempotency key is reused across retries of
 * the same intent (a lost response must not create a second signature). It is renewed
 * only after a success or a definite server rejection - never merely because the user
 * edited the input after a network failure, because the first request may have landed.
 * Callers that persist an intent across reloads can pass their own key to run().
 */
export function useCommand<C extends CommandName>(name: C) {
  const qc = useQueryClient()
  const keyRef = useRef<string>(newIdempotencyKey())
  const mutation = useMutation<CommandResult, PtwError, { args: CommandArgs[C]; expectedRowVersion?: number | null }>({
    mutationFn: async ({ args, expectedRowVersion }) => {
      try {
        return await runCommand(name, args, { expectedRowVersion, idempotencyKey: keyRef.current })
      } catch (e) {
        throw PtwError.from(e)
      }
    },
    onSuccess: async () => {
      keyRef.current = newIdempotencyKey()
      await qc.invalidateQueries()
    },
    onError: (e) => {
      // A definite rejection means nothing was recorded: a fresh key is safe and
      // avoids an IDEMPOTENCY_CONFLICT when the user corrects the input.
      if (e.code !== 'NETWORK') keyRef.current = newIdempotencyKey()
    },
  })
  return {
    ...mutation,
    run: (args: CommandArgs[C], expectedRowVersion?: number | null, opts?: { idempotencyKey?: string }) => {
      if (opts?.idempotencyKey) keyRef.current = opts.idempotencyKey
      return mutation.mutateAsync({ args, expectedRowVersion })
    },
    resetIntent: () => {
      keyRef.current = newIdempotencyKey()
      mutation.reset()
    },
  }
}
